import { UserManager, WebStorageStateStore } from 'oidc-client-ts';

// El gestor de la sesión. **Uno solo, y exportado a propósito.**
//
// Es la única fuente del token en toda la aplicación. React lo usa a través de
// `react-oidc-context`, y `api.ts` lo usa directo para firmar cada petición.
// Si hubiera dos gestores, habría dos sesiones que se renuevan por separado y
// se pisan la una a la otra en el almacenamiento.
//
// ## Por qué oidc-client-ts y no keycloak-js
//
// Dos razones, y la segunda es la que duele si se elige mal:
//
// - `keycloak-js` ata el código al proveedor. Esto habla OIDC a secas, así que
//   cambiar de servidor de identidad es cambiar configuración, no código.
// - `keycloak-js` renueva apoyándose en un iframe de sesión, y los navegadores
//   modernos bloquean cookies de terceros: falla de formas difíciles de
//   diagnosticar. Acá se renueva con el refresh token, sin iframes.

/// De dónde sale la configuración. Los tres valores son públicos —viajan en la
/// URL del navegador al entrar—, así que no hay secreto que proteger acá: van
/// en variables de Vite para que cada entorno apunte a su Keycloak.
const url = import.meta.env.VITE_KEYCLOAK_URL ?? 'http://localhost:8080';
const realm = import.meta.env.VITE_KEYCLOAK_REALM ?? 'tallerya';
const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? 'tallerya-admin';

/// El realm es **uno solo para todos los talleres**: el taller viaja en el
/// token, no en la dirección. Dar de alta un cliente del SaaS no toca esto.
export const autoridad = `${url.replace(/\/$/, '')}/realms/${realm}`;

export const gestorSesion = new UserManager({
  authority: autoridad,
  client_id: clientId,

  // A dónde vuelve Keycloak después de identificar a la persona. Se arma con el
  // origen actual en vez de escribirlo: el mismo código sirve en localhost y en
  // `admin.dev.tallerya.app`, que son las dos direcciones que el realm ya acepta.
  redirect_uri: `${window.location.origin}/`,
  post_logout_redirect_uri: window.location.origin,

  // Código de autorización con PKCE, que es lo que el realm exige (`S256`).
  // Un cliente público **no puede** guardar un secreto —vive en el navegador,
  // donde cualquiera lo lee—, y PKCE es lo que lo reemplaza: quien intercepte
  // el código no puede canjearlo sin el verificador que nunca salió de acá.
  response_type: 'code',
  scope: 'openid profile email',

  // **La sesión vive en sessionStorage.**
  //
  // Sobrevive a recargar la página, que es el criterio de F-5, y muere al
  // cerrar la pestaña. Se eligió sobre `localStorage` por dónde se usa esto:
  // la computadora de recepción es compartida, y ahí cerrar la pestaña tiene
  // que cerrar la sesión. De paso, si algún día entra un XSS, la ventana en la
  // que puede robar un token es mucho más corta.
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),

  // **La renovación silenciosa no es un extra: es lo que hace que la sesión
  // exista.** El access token del realm dura CINCO MINUTOS. Sin esto, quien
  // esté llenando un formulario largo queda fuera a mitad de camino.
  //
  // La sesión completa aguanta 30 minutos de inactividad y 10 horas en total,
  // así que una jornada entera se cubre sin volver a entrar.
  automaticSilentRenew: true,

  // Renueva **un minuto antes** de que venza, no al vencer. Con cinco minutos
  // de vida, esperar al último segundo deja al usuario con peticiones fallando
  // mientras la renovación viaja.
  accessTokenExpiringNotificationTimeInSeconds: 60,

  // Apagado a propósito: vigila la sesión con un iframe contra Keycloak, y eso
  // depende de cookies de terceros que Safari bloquea de fábrica y Chrome está
  // retirando. Encendido, produce cierres de sesión falsos que no se reproducen
  // en la máquina de quien los investiga.
  monitorSession: false,
});
